/**
 * context/EntitlementContext.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Runtime gate for Phase-8 feature entitlements. Mirrors the server-side
 * decision tree in services/entitlementService.js:
 *
 *   1. Platform admins (SUPERUSER / ADMIN / SUPERVISOR) → all modules allowed.
 *   2. Vendor owners (user IS the vendor) → all modules allowed for own data.
 *   3. Vendor sub-staff → consult the `modules` verdict map from
 *      GET /api/entitlements/me.
 *
 * The context fetches /api/entitlements/me once on mount (after a valid
 * session is detected) and exposes:
 *
 *   - isEntitled(moduleKey): Boolean
 *   - getEntitlement(moduleKey): the full verdict object (allowed, status,
 *     tier, limits, reason, bypassedAs)
 *   - bypassesEntitlements: true for platform admins
 *   - role, vendorId, loading, error, refresh()
 *
 * Fail modes:
 *   - During the initial fetch we return an OPTIMISTIC verdict for platform
 *     admins (derived from localStorage) so the dashboard renders without
 *     a flash of "denied". Non-admins see a neutral loading state — their
 *     gated pages render their skeleton until the verdict arrives.
 *   - Network errors for non-admins result in allowed=false so the UI
 *     remains closed — matches backend invariant.
 *
 * @see wiki/AUDIT_PHASE8_ENTITLEMENTS_PROGRESS.md
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiGetMyEntitlements } from "../api/index.js";
import { getUserFromLocalStorage } from "../utilities/user.js";
import { getTokenFromLocalStorage, isTokenExpired } from "../utilities/tokenUtils.js";
import { FEATURE_MODULE_LIST } from "../constants/featureModules.js";

const EntitlementContext = createContext(null);

const PLATFORM_ADMIN_ROLES = new Set(["SUPERUSER", "ADMIN", "SUPERVISOR"]);

const DENIED_VERDICT = Object.freeze({
  allowed: false,
  reason: "LOADING",
  status: null,
  tier: null,
  limits: null,
  bypassedAs: null,
});

const ADMIN_VERDICT = Object.freeze({
  allowed: true,
  reason: "PLATFORM_ADMIN_BYPASS",
  status: "ACTIVE",
  tier: "ENTERPRISE",
  limits: null,
  bypassedAs: "PLATFORM_ADMIN",
});

const isLocalAdmin = (userRecord) => {
  const role =
    userRecord?.result?.user_role ||
    userRecord?.user_role ||
    userRecord?.role ||
    "";
  return PLATFORM_ADMIN_ROLES.has(String(role).toUpperCase());
};

export const EntitlementProvider = ({ children }) => {
  const [state, setState] = useState({
    loading: true,
    error: null,
    bypassesEntitlements: false,
    role: null,
    vendorId: null,
    modules: null,
  });

  const load = useCallback(async () => {
    try {
      const token = getTokenFromLocalStorage();
      if (!token || isTokenExpired(token)) {
        // No session — nothing to fetch. PrivateRoute will redirect to /auth.
        setState((s) => ({ ...s, loading: false, modules: null }));
        return;
      }

      // Optimistic local hint: admin role is visible in the JWT payload we
      // cached at login, so first paint does not need to wait on the fetch.
      const localUser = getUserFromLocalStorage();
      const localAdmin = isLocalAdmin(localUser);
      if (localAdmin) {
        setState((s) => ({ ...s, bypassesEntitlements: true }));
      }

      // A 3PL identity is not a `Users` row: a partner is a ThreePLPartner and
      // their staff are ThreePLStaff. Both endpoints below resolve the caller
      // with `Users.findById`, so for these accounts they can only ever answer
      // 401 and 404. Asking anyway put two red errors in every partner's
      // console on every page load and taught them to ignore the console.
      const mode = localUser?.result?.operationMode;
      if (mode === "3PL_PARTNER" || mode === "3PL_STAFF") {
        setState((s) => ({ ...s, loading: false, modules: null }));
        return;
      }

      const { data } = await apiGetMyEntitlements();
      if (data && data.success) {
        setState({
          loading: false,
          error: null,
          bypassesEntitlements: Boolean(data.bypassesEntitlements),
          role: data.role || null,
          vendorId: data.vendorId || null,
          modules: data.modules || null,
        });
      } else {
        setState((s) => ({
          ...s,
          loading: false,
          error: "Entitlement fetch returned an unexpected payload.",
        }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[EntitlementContext] fetch failed:", err?.message);
      setState((s) => ({
        ...s,
        loading: false,
        error: err?.response?.data?.message || err?.message || "Fetch failed",
      }));
    }
  }, []);

  useEffect(() => {
    load();
    // Re-fetch if the session token changes in another tab.
    const onStorage = (e) => {
      if (e.key === "token" || e.key === "profile") load();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [load]);

  const value = useMemo(() => {
    const { bypassesEntitlements, modules, loading, error, role, vendorId } = state;

    const getEntitlement = (moduleKey) => {
      if (!FEATURE_MODULE_LIST.includes(moduleKey)) return DENIED_VERDICT;
      if (bypassesEntitlements) return ADMIN_VERDICT;
      if (!modules) {
        // Verdict hasn't arrived yet. While loading, DON'T deny outright —
        // but expose loading=true so callers can render their skeleton.
        return loading ? DENIED_VERDICT : DENIED_VERDICT;
      }
      const verdict = modules[moduleKey];
      return verdict || DENIED_VERDICT;
    };

    const isEntitled = (moduleKey) => getEntitlement(moduleKey).allowed;

    return {
      loading,
      error,
      role,
      vendorId,
      bypassesEntitlements,
      getEntitlement,
      isEntitled,
      refresh: load,
      modules,
    };
  }, [state, load]);

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
};

/**
 * React hook — reads from EntitlementContext. Returns a noop-denied shape if
 * called outside the provider (never throws; prevents the entire dashboard
 * from crashing if the provider is accidentally unmounted).
 */
export const useEntitlement = () => {
  const ctx = useContext(EntitlementContext);
  if (!ctx) {
    return {
      loading: false,
      error: "EntitlementProvider missing from tree",
      role: null,
      vendorId: null,
      bypassesEntitlements: false,
      getEntitlement: () => DENIED_VERDICT,
      isEntitled: () => false,
      refresh: () => {},
      modules: null,
    };
  }
  return ctx;
};

export default EntitlementContext;
